import { datetime_to_string } from "core/utils/datetime"
import { deep_copy } from "core/utils/deep_copy"

import { AggregatedBuiltPowerPlantsByH3R4Id } from "../../data/power_plants/interface"
import { ValueByPowerType, ValueByStorageType } from "../interface"
import { calculate_model_state_at_timepoint } from "./calculate_model_state_at_timepoint"
import { ModelDataInputs, POWER_TYPES, SimulationConfig, SimulationState, STORAGE_TYPES, UserAction } from "./interface"
import { calculate_score } from "./score/calculate_score"


/**
 * In the future we should check that all the timestamps are aligned.  For now
 * we just check they contain data over some year (for power demand it happens
 * to be 2018, for capacity factor data it is 2019, and the power plant data
 * currently comes from ~May 2026).
 */
export function check_data_dependencies_for_model_v3(raw: ModelDataInputs): ModelDataInputs
{
    console.assert(raw.demand_GW_by_hour.data.length === 8760, "UK_demand_GW_2018 should have 8760 entries for each hour of the year")
    console.assert(raw.capacity_factor_data.wind.date_time_to_index.size === 8760, "capacity_factor_data.wind should have 8760 entries for each hour of the year")
    console.assert(raw.capacity_factor_data.solar.date_time_to_index.size === 8760, "capacity_factor_data.solar should have 8760 entries for each hour of the year")
    // Assert all powerplants are contained in the capacity factor data
    Object.entries(raw.initial_aggregated_power_plants_by_h3r4_id).forEach(([h3r4_cell_id, powerplants]) =>
    {
        if (powerplants.solar.count)
        {
            console.assert(raw.capacity_factor_data.solar.h3r4_cell_id_to_index.has(h3r4_cell_id), `capacity_factor_data.solar should contain data for h3r4 cell ${h3r4_cell_id}`)

        }
        if (powerplants.wind.count)
        {
            console.assert(raw.capacity_factor_data.wind.h3r4_cell_id_to_index.has(h3r4_cell_id), `capacity_factor_data.wind should contain data for h3r4 cell ${h3r4_cell_id}`)
        }
    })

    return raw
}


// TODO, should this be renamed to setup_simulation?  If it can handle the
// simulation setup being changed without recalling this then this should be
// kept as to setup_model, otherwise it should be renamed to setup_simulation.
export function setup_model(config: SimulationConfig, data_dependencies: ModelDataInputs): SimulationState
{
    function get_timestamp_index_for_datetime(datetime: Date): number
    {
        // copy the datetime so that we can change it to match capacity_factor_data.year
        datetime = new Date(datetime.getTime())
        datetime.setFullYear(data_dependencies.capacity_factor_data.year)

        const datetime_str = datetime_to_string(datetime)

        // Just use wind capacity factor date_time_to_index for now.  But should use
        // a different more generic datetime data structure
        const { date_time_to_index } = data_dependencies.capacity_factor_data.wind

        // Calculate the start and end timestamp indices based on the demand data
        const timestamp_index = date_time_to_index.get(datetime_str)
        if (timestamp_index === undefined) throw new Error(`setup_model: datetime ${datetime_str} not found (in capacity factor data)`)
        return timestamp_index
    }

    const start_timestamp_index = get_timestamp_index_for_datetime(config.start_datetime)
    const end_timestamp_index = get_timestamp_index_for_datetime(config.end_datetime)

    const built_power_plants = get_built_power_plants(deep_copy(data_dependencies.initial_aggregated_power_plants_by_h3r4_id))

    let simulation_state: SimulationState = {
        config:
        {
            ...config,
            start_timestamp_index,
            end_timestamp_index,
        },
        score: calculate_score(undefined, data_dependencies),

        demand_GW_proportion_by_h3r4: { initial: {} },
        built_power_plants,

        timesteps: [],
    }

    evolve_model_over_time_range(simulation_state, data_dependencies)

    return simulation_state
}


function get_built_power_plants(aggregated_power_plants_by_h3r4_id: AggregatedBuiltPowerPlantsByH3R4Id)
{
    const capacity_GW_by_type: ValueByPowerType<number> = {
        wind: 0,
        solar: 0,
        gas: 0,
        nuclear: 0,
        battery: 0,
        hydro_pumped_storage: 0,
        hydro_river: 0,
    }

    const storage_GWh_by_type: ValueByStorageType<number> = {
        battery: 0,
        hydro_pumped_storage: 0,
    }

    Object.values(aggregated_power_plants_by_h3r4_id).forEach(built_power_plants =>
    {
        POWER_TYPES.forEach(power_type =>
        {
            capacity_GW_by_type[power_type] += built_power_plants[power_type].capacity_MW
        })

        STORAGE_TYPES.forEach(power_type =>
        {
            storage_GWh_by_type[power_type] += built_power_plants[power_type].store_capacity_MWh
        })
    })

    POWER_TYPES.forEach(power_type =>
    {
        capacity_GW_by_type[power_type] /= 1000
    })

    STORAGE_TYPES.forEach(power_type =>
    {
        storage_GWh_by_type[power_type] /= 1000
    })

    return {
        aggregated_by_h3r4: aggregated_power_plants_by_h3r4_id,
        capacity_GW_by_type,
        storage_GWh_by_type,
    }
}


export function evolve_model_over_time_range(simulation_state: SimulationState, data_dependencies: ModelDataInputs): void
{
    simulation_state.timesteps = []

    const start_index = simulation_state.config.start_timestamp_index
    const end_index = simulation_state.config.end_timestamp_index

    for (let index = start_index; index <= end_index; ++index)
    {
        const state_at_time_point = calculate_model_state_at_timepoint(simulation_state, data_dependencies)
        simulation_state.timesteps.push(state_at_time_point)
    }

    simulation_state.score = calculate_score(simulation_state, data_dependencies)
}


export function check_action_is_valid_for_simulation_state(_simulation_state: SimulationState, _action: UserAction): { valid: boolean, reason?: string }
{
    return { valid: true }
}


export function update_simulation_state_with_user_action(state: SimulationState, data_dependencies: ModelDataInputs, action: UserAction): SimulationState
{
    let current = state.built_power_plants.aggregated_by_h3r4[action.h3r4_id]
    if (!current)
    {
        current = {
            wind: { capacity_MW: 0, count: 0, area_km2: 0 },
            solar: { capacity_MW: 0, count: 0, area_km2: 0 },
            gas: { capacity_MW: 0, count: 0, area_km2: 0 },
            nuclear: { capacity_MW: 0, count: 0, area_km2: 0 },
            battery: { capacity_MW: 0, count: 0, area_km2: 0, store_capacity_MWh: 0 },
            hydro_pumped_storage: { capacity_MW: 0, count: 0, area_km2: 0, store_capacity_MWh: 0 },
            hydro_river: { capacity_MW: 0, count: 0, area_km2: 0 },
        }
    }

    current[action.power_type].capacity_MW += MW_per_km2_for_power_type(action.power_type, action.area_addable_km2) * action.area_addable_km2
    current[action.power_type].count += 1
    current[action.power_type].area_km2 = (current[action.power_type].area_km2 || 0) + action.area_addable_km2

    // if (action.power_type === "battery" || action.power_type === "hydro_pumped_storage")
    // {
    //     current[action.power_type].store_capacity_MWh = (current[action.power_type].store_capacity_MWh || 0) + action.store_capacity_addable_MWh
    // }

    state.built_power_plants.aggregated_by_h3r4[action.h3r4_id] = current

    state.built_power_plants.capacity_GW_by_type[action.power_type] += MW_per_km2_for_power_type(action.power_type, action.area_addable_km2) * action.area_addable_km2 / 1000

    // if (action.power_type === "battery" || action.power_type === "hydro_pumped_storage")
    // {
    //     state.built_power_plants.storage_GWh_by_type[action.power_type] += action.store_capacity_addable_MWh / 1000
    // }

    evolve_model_over_time_range(state, data_dependencies)

    return state
}


function MW_per_km2_for_power_type(power_type: string, _area_km2: number): number
{
    if (power_type === "wind") return 1 // area_km2 should decrease as wind farms get larger
    if (power_type === "solar") return 5
    return 0
}
